import React, { FC, useMemo } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  CompanyIcon,
  DetailsCont,
  ReportGroupCompany,
  ReportGroupTitle,
  ReportsTileCont,
  ReportTypeCont,
  ReportTypeText,
  ResizeCont,
  OverviewCont,
  EffectTypeText,
} from './ReportsGroupTableTile.styles';
import ReportsNestedTable from './ReportsNestedGroupTable/ReportsNestedGroupTable';
import { surveyTypes } from '../../../constans/forms';
import TableTile from '../../../HOC/TableTile/TableTile';
import { useAuth } from '../../../hooks/useAuth';
import { useDate } from '../../../hooks/useDate';
import { useFilters } from '../../../hooks/useFilters';
import { IGroupOfReportsItem } from '../../../services/reports';
import CloseAll from '@/assets/icons/closeAllAccordion.svg?react';
import OpenAll from '@/assets/icons/openAllAccordion.svg?react';
import AdaptIcon from '@/assets/icons/smallAdaptIcon.svg?react';
import EffectIcon from '@/assets/icons/smallEffectIcon.svg?react';
import PulseIcon from '@/assets/icons/smallPulseIcon.svg?react';

interface IReportsTableTile {
  item: IGroupOfReportsItem;
}

const ReportsGroupTableTile: FC<IReportsTableTile> = ({
  item: { surveyName, surveyEndDate, surveyType, organization, surveyId, reports },
}) => {
  const reportsWithSearch = reports?.items ? reports?.items?.length > 0 : true;
  const reportsKey = 'reports';
  const { getNestedFilters, applyNestedFilter } = useFilters();
  const reportsTileFilters = getNestedFilters(reportsKey);
  const active = Boolean(reportsTileFilters[surveyId]?.perPage);
  const { setDateFormat } = useDate();
  const { isEBHRMember } = useAuth();
  const { t } = useTranslation();

  const renderTypeIcon = useMemo(() => {
    switch (surveyType) {
      case surveyTypes.ADAPT:
        return <AdaptIcon data-testid="adaptIcon" />;
      case surveyTypes.PULSE:
        return <PulseIcon data-testid="pulseIcon" />;
      case surveyTypes.EFFECT:
        return <EffectIcon data-testid="effectIcon" />;
      default:
        return <PulseIcon data-testid="pulseIcon" />;
    }
  }, [surveyType]);

  const resizeGroupOfReports = () => {
    const isActive = !active;
    const conditionFilters = isActive ? { perPage: '5' } : { perPage: null, sortOrder: null };
    applyNestedFilter(conditionFilters, surveyId, reportsKey);
  };

  const handleTileClick = (event: React.MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest('a, button, [role="button"]')) {
      return;
    }
    resizeGroupOfReports();
  };

  return (
    <TableTile
      isShadowed={true}
      active={active}
      body={
        <ReportsNestedTable
          reportId={surveyId}
          areReportsAvailable={reportsWithSearch}
          initialItems={reports?.items || undefined}
          metadataFromHigherReq={reports?.pagingInfo || undefined}
          surveyType={surveyType}
        />
      }
    >
      <ReportsTileCont id="ReportsTileCont" onClick={handleTileClick}>
        <ResizeCont id="ResizeCont">{active ? <CloseAll /> : <OpenAll />}</ResizeCont>
        <ReportTypeCont id="ReportTypeCont">
          {renderTypeIcon}
          {surveyType == 'EFFECT' ? (
            <EffectTypeText>{surveyType}</EffectTypeText>
          ) : (
            <ReportTypeText id="ReportTypeText" data-testid="reportTypeText">
              {surveyType || surveyTypes.PULSE}
            </ReportTypeText>
          )}
        </ReportTypeCont>
        <OverviewCont id="OverviewCont">
          <ReportGroupTitle id="ReportGroupTitle">{surveyName}</ReportGroupTitle>
          <DetailsCont id="DetailsCont">
            {isEBHRMember ? (
              <>
                <CompanyIcon id="CompanyIcon" data-testid="companyIcon" />
                {`${t('reports.organisation')}:`}
                <ReportGroupCompany id="ReportGroupCompany" data-testid="reportGroupCompany">
                  {` ${organization}`}
                </ReportGroupCompany>
              </>
            ) : null}
            <span id="EndDate" data-testid="endDate">
              {setDateFormat(surveyEndDate)}
            </span>
          </DetailsCont>
        </OverviewCont>
      </ReportsTileCont>
    </TableTile>
  );
};

export default ReportsGroupTableTile;
